import { useMemo, useState } from 'react';

import {
  Button,
  Icon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';
import { SupportedLanguages } from '@eluve/language-utils';

import { DeletePromptTemplateDefaultDialog } from '../DeletePromptTemplateDefaultDialog';
import {
  PromptTemplateDefaultDialog,
  PromptTemplateDefaultForEdit,
} from '../PromptTemplateDefaultDialog';
import type {
  LlmOutputType,
  OutputTemplateOption,
} from '../PromptTemplateDefaultForm';
import { PromptTemplateReference } from '../promptTemplateDefaults.operations';

import { EmptyState } from './EmptyState';
import { PanelHeader } from './PanelHeader';
import type { PromptTemplateDefault } from './index';

interface DefaultDetailPanelProps {
  defaults: PromptTemplateDefault[];
  entityType: 'global' | 'org' | 'user';
  tenantId?: string;
  userId?: string;
  llmOutputTypes: LlmOutputType[];
  promptTemplates: PromptTemplateReference[];
  outputTemplates?: OutputTemplateOption[];
  onRefreshTemplates?: () => void;
  selectedOutputType?: string | null;
  onOpenDialog: () => void;
  dialogOpen: boolean;
  onDialogOpenChange: (open: boolean) => void;
}

// Grid column definitions for consistent widths
const GRID_COLS_WITH_SPECIALTY = 'minmax(0,1fr) 200px 100px 100px';
const GRID_COLS_WITHOUT_SPECIALTY = 'minmax(0,1fr) 100px 100px';

const DefaultGridRow: React.FC<{
  ptDefault: PromptTemplateDefault;
  onEdit: () => void;
  showSpecialty: boolean;
}> = ({ ptDefault, onEdit, showSpecialty }) => {
  const displaySpecialty = ptDefault.specialty ?? 'Any';
  const displayLanguage = ptDefault.language
    ? (SupportedLanguages[ptDefault.language] ?? ptDefault.language)
    : 'Any';

  return (
    <div
      className="grid cursor-pointer items-center border-b border-border-tertiary last:border-b-0 hover:bg-bg-tertiary"
      style={{
        gridTemplateColumns: showSpecialty
          ? GRID_COLS_WITH_SPECIALTY
          : GRID_COLS_WITHOUT_SPECIALTY,
      }}
      onClick={onEdit}
    >
      <div className="px-3 py-2">
        <Stack gap={2} direction="horizontal">
          <Icon
            name="FileText"
            size="sm"
            className="shrink-0 text-text-tertiary"
          />
          <Text inline size="xs" className="truncate">
            {ptDefault.prompt_template.name}
          </Text>
          {ptDefault.customInstructions && (
            <Icon
              name="CheckCircle2"
              size="sm"
              className="shrink-0 text-text-positive"
            />
          )}
        </Stack>
      </div>
      {showSpecialty && (
        <div className="px-3 py-2">
          <Text size="xs" color="secondary" className="truncate">
            {displaySpecialty}
          </Text>
        </div>
      )}
      <div className="px-3 py-2">
        <Text size="xs" color="secondary">
          {displayLanguage}
        </Text>
      </div>
      <div className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <DeletePromptTemplateDefaultDialog
          defaultId={ptDefault.id}
          userId={ptDefault.userId}
        />
      </div>
    </div>
  );
};

export const DefaultDetailPanel: React.FC<DefaultDetailPanelProps> = ({
  defaults,
  entityType,
  tenantId,
  userId,
  llmOutputTypes,
  promptTemplates,
  outputTemplates,
  onRefreshTemplates,
  selectedOutputType,
  onOpenDialog,
  dialogOpen,
  onDialogOpenChange,
}) => {
  const isGlobal = entityType === 'global';
  const isOrg = entityType === 'org';
  const showSpecialty = isGlobal || isOrg;
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [editingDefault, setEditingDefault] =
    useState<PromptTemplateDefaultForEdit | null>(null);

  // At tenant/user scope, prevent adding both SOAP_NOTE and DYNAMIC_OUTPUT.
  // If one exists, mark the other as disabled with a reason.
  const disabledOutputTypes = useMemo(() => {
    if (isGlobal || isOrg) return {};

    const existingTypes = new Set(defaults.map((d) => d.llmOutputType));
    const disabled: Record<string, string> = {};

    if (existingTypes.has('SOAP_NOTE')) {
      disabled['DYNAMIC_OUTPUT'] =
        'Cannot add DYNAMIC_OUTPUT because SOAP_NOTE defaults already exist. Remove SOAP_NOTE defaults first.';
    }
    if (existingTypes.has('DYNAMIC_OUTPUT')) {
      disabled['SOAP_NOTE'] =
        'Cannot add SOAP_NOTE because DYNAMIC_OUTPUT defaults already exist. Remove DYNAMIC_OUTPUT defaults first.';
    }
    return disabled;
  }, [defaults, isGlobal, isOrg]);

  // Filter and sort defaults by selected output type, specialty, then language
  const sortedDefaults = useMemo(() => {
    const filtered = selectedOutputType
      ? defaults.filter((d) => d.llmOutputType === selectedOutputType)
      : defaults;

    return [...filtered].sort((a, b) => {
      // Sort by specialty first
      const specA = a.specialty ?? 'Any';
      const specB = b.specialty ?? 'Any';
      if (specA !== specB) return specA.localeCompare(specB);

      // Then by language
      const langA = a.language ?? 'Any';
      const langB = b.language ?? 'Any';
      return langA.localeCompare(langB);
    });
  }, [defaults, selectedOutputType]);

  const title = isGlobal || isOrg ? 'Details' : 'Override Details';

  const handleAddClick = () => {
    setDialogMode('add');
    setEditingDefault(null);
    onOpenDialog();
  };

  const handleEditClick = (ptDefault: PromptTemplateDefault) => {
    setDialogMode('edit');
    setEditingDefault({
      id: ptDefault.id,
      tenantId: ptDefault.tenantId,
      userId: ptDefault.userId,
      llmOutputType: ptDefault.llmOutputType,
      specialty: ptDefault.specialty,
      language: ptDefault.language,
      promptTemplateId: ptDefault.promptTemplateId,
      outputTemplateId: (ptDefault.outputTemplateId as string | null) ?? null,
      customInstructions: ptDefault.customInstructions,
    });
    onOpenDialog();
  };

  const handleDialogOpenChange = (open: boolean) => {
    onDialogOpenChange(open);
    if (!open) {
      setEditingDefault(null);
    }
  };

  return (
    <>
      <Stack align="stretch" fullHeight>
        <PanelHeader
          title={title}
          sticky
          cornerRadius="tr"
          action={
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddClick}
                  aria-label={
                    isGlobal || isOrg ? 'Add Default' : 'Add Override'
                  }
                >
                  <Icon name="Plus" size="auto" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isGlobal || isOrg ? 'Add Default' : 'Add Override'}
              </TooltipContent>
            </Tooltip>
          }
        />

        {/* Content - Grid Layout */}
        {sortedDefaults.length === 0 ? (
          <EmptyState message="No defaults configured" />
        ) : (
          <div className="overflow-x-auto">
            {/* Header */}
            <div
              className="grid border-b border-border-tertiary bg-bg-tertiary text-sm"
              style={{
                gridTemplateColumns: showSpecialty
                  ? GRID_COLS_WITH_SPECIALTY
                  : GRID_COLS_WITHOUT_SPECIALTY,
              }}
            >
              <div className="px-3 py-2 font-medium text-text-secondary">
                Template
              </div>
              {showSpecialty && (
                <div className="px-3 py-2 font-medium text-text-secondary">
                  Specialty
                </div>
              )}
              <div className="px-3 py-2 font-medium text-text-secondary">
                Language
              </div>
              <div className="px-3 py-2"></div>
            </div>
            {/* Rows */}
            {sortedDefaults.map((ptDefault) => (
              <DefaultGridRow
                key={ptDefault.id}
                ptDefault={ptDefault}
                onEdit={() => handleEditClick(ptDefault)}
                showSpecialty={showSpecialty}
              />
            ))}
          </div>
        )}
      </Stack>

      <PromptTemplateDefaultDialog
        mode={dialogMode}
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        entityType={entityType}
        tenantId={tenantId}
        userId={userId}
        llmOutputTypes={llmOutputTypes}
        disabledOutputTypes={disabledOutputTypes}
        promptTemplates={promptTemplates}
        outputTemplates={outputTemplates}
        onRefreshTemplates={onRefreshTemplates}
        defaultToEdit={editingDefault ?? undefined}
        preselectedOutputType={selectedOutputType}
      />
    </>
  );
};
